import { StructuredOutputDto } from '@shared/services/apiService/apiService';

import { getPrimaryCampaignStructuredOutput } from './call-analysis-structured-output';

export type AnalysisFieldType = 'string' | 'number' | 'integer' | 'boolean' | 'other';

export interface AnalysisField {
  name: string;
  type: AnalysisFieldType;
  enumValues?: string[];
}

type ParsedProperty = {
  type?: unknown;
  enum?: unknown;
};

type ParsedSchema = {
  type?: string;
  properties?: Record<string, unknown>;
};

const toFieldType = (rawType: unknown): AnalysisFieldType => {
  if (
    rawType === 'string' ||
    rawType === 'number' ||
    rawType === 'integer' ||
    rawType === 'boolean'
  ) {
    return rawType;
  }

  return 'other';
};

const toEnumValues = (rawEnum: unknown): string[] | undefined => {
  if (!Array.isArray(rawEnum)) {
    return undefined;
  }

  const values = rawEnum.filter((item): item is string => typeof item === 'string');

  return values.length > 0 ? values : undefined;
};

const MAX_NESTING_DEPTH = 5;

const parseSchema = (schema?: string | null): ParsedSchema | null => {
  if (!schema) {
    return null;
  }

  try {
    const parsed = JSON.parse(schema) as ParsedSchema;

    return parsed?.type === 'object' && parsed.properties ? parsed : null;
  } catch {
    return null;
  }
};

const collectLeaves = (
  properties: Record<string, unknown>,
  prefix: string,
  depth: number,
): AnalysisField[] =>
  Object.entries(properties).flatMap(([name, rawProperty]) => {
    const property = (rawProperty ?? {}) as ParsedProperty;
    const path = prefix ? `${prefix}.${name}` : name;

    if (property.type === 'object' && depth < MAX_NESTING_DEPTH) {
      const nested = (property as ParsedSchema).properties;

      return nested ? collectLeaves(nested, path, depth + 1) : [];
    }

    return [
      {
        name: path,
        type: toFieldType(property.type),
        enumValues: toEnumValues(property.enum),
      },
    ];
  });

export const extractAnalysisFields = (schema?: string | null): AnalysisField[] => {
  const parsed = parseSchema(schema);

  return parsed ? collectLeaves(parsed.properties!, '', 0) : [];
};

export const extractAnalysisFieldNames = (schema?: string | null): string[] => {
  const parsed = parseSchema(schema);

  return parsed ? Object.keys(parsed.properties!) : [];
};

export const getCampaignAnalysisFields = (
  structuredOutputs?: StructuredOutputDto[],
): AnalysisField[] => {
  const { primaryStructuredOutput } = getPrimaryCampaignStructuredOutput(structuredOutputs);

  return extractAnalysisFields(primaryStructuredOutput?.schema);
};

export const getCampaignAnalysisFieldNames = (
  structuredOutputs?: StructuredOutputDto[],
): string[] => {
  const { primaryStructuredOutput } = getPrimaryCampaignStructuredOutput(structuredOutputs);

  return extractAnalysisFieldNames(primaryStructuredOutput?.schema);
};
